/**
 * Image upload component for Formik forms.
 * Compresses image & thumbnail, stores as base64, supports removal & re-upload.
 */

import React, { useEffect, useState } from "react";
import { useField, useFormikContext } from "formik";
import { FormattedMessage, injectIntl } from "react-intl";
import imageCompression from "browser-image-compression";
import { getBase64 } from "../../constants/imagePicker.helper";
import FormikFormErrorHandler from "./FormikFormErrorHandler";
import { convertFileToWebP } from "../../constants/constants";

interface IProps {
  name: string;
  intl: any;
  disabled?: boolean;
  isConvertImageToBase64?: boolean;
  label?: string;
}

const FormikFormUploadImages = (props: IProps) => {
  const [field, meta, helpers] = useField(props.name);
  const { setFieldValue } = useFormikContext<any>();
  const [focus, setFocus] = useState(false);

  const options = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 200,
    useWebWorker: true,
  };

  const thumbnailOptions = {
    maxSizeMB: 0.1,
    maxWidthOrHeight: 30,
    useWebWorker: true,
  };

  const inputId = `imgupload-${props.name}`;

  useEffect(() => {
    if (field.value) setFocus(true);
  }, [field.value]);

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
  
    try {
      const compressedImage = await imageCompression(file, options);
      let base64Image;
      if(props?.isConvertImageToBase64){
        const webpImage = await convertFileToWebP(compressedImage);
        base64Image = await getBase64(compressedImage);
      }
      else{
        base64Image = await getBase64(compressedImage);
      }
      helpers.setValue(base64Image);
  
      const compressedThumbnail = await imageCompression(file, thumbnailOptions);
      const base64Thumbnail = await getBase64(compressedThumbnail);
      setFieldValue(`${props.name}Thumbnail`, base64Thumbnail);
  
      event.target.value = ""; 
    } catch (error) {
      helpers.setError("Error uploading image");
    }
  };
  

  const removeImage = () => {
    helpers.setValue("");
    setFieldValue(`${props.name}Thumbnail`, "");
  };

  return (
    <>
      <input
        id={inputId}
        name={props.name}
        type="file"
        accept="image/*"
        disabled={props.disabled}
        onChange={handleImageChange}
        style={{ display: "none" }}
      />

      {!field.value && (
        <label htmlFor={inputId}>
          <div className="position-relative upload-size-image">
            <div className="user-profile-box">
              <div className="upload-profile align-middle">
                <i className="bi bi-plus-lg"></i>
                <p className="text-center">
                  <FormattedMessage id="upload_image" />
                </p>
              </div>
            </div>
          </div>
        </label>
      )}

      {field.value && (
        <div className="position-relative upload-size-image uploaded-image">
          <label htmlFor={inputId}></label>
          <img
            src={field.value}
            alt="Uploaded"
            className="user-profile-box glow-on-hover"
          />
          <button
            type="button"
            className="remove-img"
            onClick={removeImage}
          >
            <svg id="fi_9759080" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <g>
                <path d="m16 2c-18.5.6-18.5 27.4 0 28 18.5-.6 18.5-27.4 0-28z" fill="#f44336" />
                <path d="m17.4 16 4.2-4.2c.9-.9-.5-2.3-1.4-1.4l-4.2 4.2-4.2-4.2c-.9-.9-2.3.5-1.4 1.4l4.2 4.2-4.2 4.2c-.9.9.5 2.3 1.4 1.4l4.2-4.2 4.2 4.2c.9.9 2.3-.5 1.4-1.4z" fill="#eee" />
              </g>
            </svg>
          </button>
        </div>
      )}

      <FormikFormErrorHandler intl={props.intl} meta={meta} />
    </>
  );
};

export default injectIntl(FormikFormUploadImages);
